import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { gradient, gradientText } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("owner@dispensary.com");
  const [password, setPassword] = useState("owner123");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password, pin || undefined);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className={`hidden lg:flex lg:w-1/2 ${gradient} relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur">
              <Leaf className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>HA Grove ST</span>
          </div>
          <div>
            <h1 className="text-4xl font-bold leading-tight mb-3" style={{ fontFamily: "Outfit" }}>
              Point of Sale, built for the dispensary counter.
            </h1>
            <p className="text-white/80 text-base max-w-md">
              Fast checkout, safe inventory transfers, cash drawer control, and real-time website sync — all in one place.
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/70 text-sm">
            <ShieldCheck className="w-4 h-4" /> Single-location · Grove ST
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-6">
            <Leaf className="w-6 h-6 text-indigo-600" />
            <span className={`text-2xl font-bold ${gradientText}`} style={{ fontFamily: "Outfit" }}>HA Grove ST</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">Employees only. Use your credentials.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pin">PIN (optional)</Label>
            <Input id="pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)}
              data-testid="login-pin-input" placeholder="••••" />
          </div>
          <Button type="submit" disabled={busy} className={`w-full h-11 ${gradient} hover:opacity-90 text-white font-semibold`}
            data-testid="login-submit-btn">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
